import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, ExternalLink, Download, Sparkles, Brain, Eye,
  Calendar, Euro, MapPin, Building2, Tag, FileText, BookmarkPlus, BookmarkCheck,
  Loader2, AlertCircle, Users, AlertTriangle,
} from 'lucide-react';
import {
  getTenderDetail, triggerTenderDownload, analyzeTender,
  addToTenderWatchlist, removeFromTenderWatchlist,
  getTenderAttachmentPdfUrl,
  generateOfferFromTender, generateSwzResponse,
  setTenderOverride, removeTenderOverride, getTenderOverride,
  getTenderChanges,
} from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import AnalysisPanel from '../components/tenders/AnalysisPanel';
import ConsiliumPanel from '../components/tenders/ConsiliumPanel';
import PredictionPanel from '../components/tenders/PredictionPanel';
import ActionsPanel from '../components/tenders/ActionsPanel';
import PricingPanel from '../components/tenders/PricingPanel';
import QAPanel from '../components/tenders/QAPanel';
import DecisionChainPanel from '../components/tenders/DecisionChainPanel';
import TimelinePanel from '../components/tenders/TimelinePanel';
import ChangesPanel from '../components/tenders/ChangesPanel';
import PlanLock from '../components/common/PlanLock';
import FollowButton from '../components/common/FollowButton';
import AwardOutcomeDialog from '../components/tenders/AwardOutcomeDialog';
import MarkSubmittedDialog from '../components/tenders/MarkSubmittedDialog';
import IntelBadge from '../components/intel/IntelBadge';
import ConnectionsPanel from '../components/intel/ConnectionsPanel';
import { enrichTenderAuthority } from '../lib/api';
import { markMissionComplete, MISSIONS } from '../lib/tutorialActions';

const SOURCE_LABELS = { bzp: 'BZP Polska', ted: 'TED Europa', auction: 'Licytacje' };

function fmtMoney(v, c = 'PLN') {
  if (!v) return '—';
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' ' + c;
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    // TED bug guard: "2026-04-13+02:00 2026-04-13+02:00..." → take first token
    const clean = String(s).trim().split(/\s+/)[0];
    let isoLike = clean;
    // "2026-04-13+02:00" (date+tz only) → "2026-04-13T00:00:00+02:00"
    if (!clean.includes('T') && /\+\d{2}:\d{2}$/.test(clean)) {
      const [d, tz] = clean.split('+');
      if (d.length === 10) isoLike = `${d}T00:00:00+${tz}`;
    }
    const d = new Date(isoLike);
    if (isNaN(d.getTime())) return clean.slice(0, 10);
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return String(s).slice(0, 10);
  }
}

export default function TenderDetail({ lang, tenantId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(null);
  const [autoFetched, setAutoFetched] = useState(false);
  const [unackChanges, setUnackChanges] = useState(0);

  // MASKIL Agent — "Wygeneruj odpowiedź na SWZ" (D-track 2026-05-26)
  const [swzResponseDialog, setSwzResponseDialog] = useState(false);
  const [swzResponseText, setSwzResponseText] = useState('');
  const [swzResponseMeta, setSwzResponseMeta] = useState(null);
  const [extraNotes, setExtraNotes] = useState('');
  const [userCompany, setUserCompany] = useState('');

  // Probe unacknowledged-changes count for the tab badge (lightweight, fire-and-forget)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getTenderChanges(id)
      .then((r) => { if (!cancelled) setUnackChanges(r.unacknowledged_count || 0); })
      .catch(() => { /* 404 OK */ });
    return () => { cancelled = true; };
  }, [id]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getTenderDetail(id);
      setData(d);
    } catch (e) {
      toast.error('Nie znaleziono przetargu: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  // Auto-fetch SWZ for high-match tenders without attachments yet (#1 — Adam-handlowiec proactive)
  useEffect(() => {
    if (autoFetched) return;
    if (!data?.opportunity) return;
    const score = data.opportunity.match_score || 0;
    const hasAttachments = (data.attachments || []).length > 0;
    if (score >= 70 && !hasAttachments && data.opportunity.source !== 'auction') {
      setAutoFetched(true);
      toast.info(`Match score ${score} — auto-pobieram SWZ...`);
      triggerTenderDownload(id)
        .then(r => {
          if (r.downloaded > 0) toast.success(`Auto-pobrano ${r.downloaded} załączników`);
          fetch();
        })
        .catch(() => {/* silent: keep manual button visible */});
    }
  }, [data, autoFetched, id, fetch]);

  const handleDownload = async () => {
    setActionLoading('download');
    try {
      const r = await triggerTenderDownload(id);
      toast.success(`Pobrano ${r.downloaded} załączników`);
      await fetch();
    } catch (e) {
      toast.error('Pobranie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleAnalyze = async () => {
    setActionLoading('analyze');
    try {
      toast.info('Analizuję SWZ przez Claude Sonnet...');
      await analyzeTender(id);
      toast.success('Analiza NLP ukończona');
      markMissionComplete(MISSIONS.ANALYZE_TENDER);
      await fetch();
    } catch (e) {
      toast.error('Analiza nie powiodła się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateSwzResponse = async () => {
    setActionLoading('swz-response');
    setSwzResponseText('');
    setSwzResponseMeta(null);
    setSwzResponseDialog(true);
    try {
      toast.info('Adam generuje odpowiedź na SWZ (opus-4-7)…');
      const r = await generateSwzResponse(id, {
        user_company: userCompany || undefined,
        extra_notes: extraNotes || undefined,
      });
      setSwzResponseText(r.text || '');
      setSwzResponseMeta({
        model: r.model,
        role: r.role_detected,
        input_tokens: r.usage?.input_tokens || 0,
        output_tokens: r.usage?.output_tokens || 0,
        cache_hit: r.cache?.hit,
        ctx: r.context_used,
      });
      toast.success(`Wygenerowane (${r.model})`);
    } catch (e) {
      toast.error('Generowanie nie powiodło się: ' + (e.response?.data?.detail || e.message));
      setSwzResponseDialog(false);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopySwz = async () => {
    try {
      await navigator.clipboard.writeText(swzResponseText);
      toast.success('Skopiowano do schowka');
    } catch {
      toast.error('Nie udało się skopiować — zaznacz tekst ręcznie');
    }
  };

  const handleGenerateOffer = async () => {
    setActionLoading('offer');
    try {
      toast.info('Generuję ofertę z katalogu produktów...');
      const r = await generateOfferFromTender(id);
      const msg = `Oferta ${r.offer_number} (${r.products_matched} produktów dopasowanych${r.contractor_auto_created ? ', zamawiający auto-utworzony' : ''})`;
      toast.success(msg);
      markMissionComplete(MISSIONS.GENERATE_OFFER);
      if (r.warning) toast.warning(r.warning);
      navigate(`/offers/${r.offer_id}`);
    } catch (e) {
      toast.error('Generowanie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const setOverride = async (overrideType) => {
    setActionLoading('override');
    try {
      if (overrideType === null) {
        await removeTenderOverride(id);
        toast.success('Override usunięty — wraca scoring algorytmiczny');
      } else {
        await setTenderOverride(id, { override_type: overrideType, notes: '' });
        toast.success(overrideType === 'priority'
          ? '🔥 Oznaczono jako priorytet — zostanie pinowane na top listy'
          : '🔇 Oznaczono jako nieistotne — nie będzie pokazywane w wynikach'
        );
      }
      await fetch();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const toggleWatch = async () => {
    setActionLoading('watch');
    try {
      if (data.is_watched) {
        await removeFromTenderWatchlist(id);
        toast.success('Usunięto z obserwowanych');
      } else {
        await addToTenderWatchlist({
          opportunity_id: id,
          source: data.opportunity.source,
        });
        toast.success('Dodano do obserwowanych');
      }
      await fetch();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (!data?.opportunity) {
    return (
      <Card>
        <CardContent className="pt-10 pb-10 text-center">
          <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p>Przetarg nie znaleziony</p>
          <Button variant="outline" className="mt-3" onClick={() => navigate('/tenders')}>
            Powrót do listy
          </Button>
        </CardContent>
      </Card>
    );
  }

  const t = data.opportunity;
  const attachments = data.attachments || [];
  const analysis = data.analysis;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenders')}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Powrót
          </Button>
          <h1 className="text-xl font-bold flex items-center gap-3 flex-wrap">
            {t.match_score != null && (
              <span
                className={`text-xs font-bold px-2 py-1 rounded border shrink-0 ${
                  t.match_label === 'high' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                  t.match_label === 'medium' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                  'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                }`}
                title={`Match: ${t.match_score}/100 — ${t.match_label}. Słowa: ${(t.matched_keywords || []).join(', ') || '—'}`}
              >
                {t.match_score}
              </span>
            )}
            {t.stage && t.stage_meta && (
              <span
                className="text-xs font-medium px-2 py-1 rounded border shrink-0 flex items-center gap-1"
                style={{
                  borderColor: t.stage_meta.color + '60',
                  background: t.stage_meta.color + '20',
                  color: t.stage_meta.color,
                }}
                title={t.stage_meta.description}
              >
                {t.stage_meta.icon} {t.stage_meta.label}
              </span>
            )}
            {t.win_rate?.score != null && (
              <span
                className={`text-xs font-bold px-2 py-1 rounded border shrink-0 flex items-center gap-1 ${
                  t.win_rate.score >= 60 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                  t.win_rate.score >= 30 ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                  'bg-red-500/15 text-red-300 border-red-500/30'
                }`}
                title={`Predykcja wygranej: ${t.win_rate.score}% (${t.win_rate.label || 'computed'}) — z modelu ML opartego o historyczne wygrane w podobnych przetargach`}
              >
                🎯 {t.win_rate.score}%
              </span>
            )}
            <span>{t.title}</span>
          </h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1.5">
            <Badge variant="outline">{SOURCE_LABELS[t.source] || t.source}</Badge>
            {t.contracting_authority && (
              <div className="flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                {Array.isArray(t.contracting_authority) ? t.contracting_authority.join(', ') : t.contracting_authority}
              </div>
            )}
            {t.region && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {t.region}{t.city && `, ${t.city}`}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(() => {
            const ov = data.opportunity?.override_type;
            return (
              <>
                <Button
                  variant={ov === 'priority' ? 'default' : 'outline'}
                  size="sm"
                  className={ov === 'priority' ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-400 hover:text-red-300'}
                  onClick={() => setOverride(ov === 'priority' ? null : 'priority')}
                  disabled={actionLoading === 'override'}
                  title="Oznacz jako priorytet — zawsze na topie listy"
                >
                  🔥 {ov === 'priority' ? 'Priorytet ON' : 'Priorytet'}
                </Button>
                <Button
                  variant={ov === 'irrelevant' ? 'default' : 'outline'}
                  size="sm"
                  className={ov === 'irrelevant' ? 'bg-zinc-600 hover:bg-zinc-700 text-white' : 'text-muted-foreground'}
                  onClick={() => setOverride(ov === 'irrelevant' ? null : 'irrelevant')}
                  disabled={actionLoading === 'override'}
                  title="Oznacz jako nieistotne — nie pokazuj w wynikach"
                >
                  🔇 {ov === 'irrelevant' ? 'Ukryte' : 'Nieistotne'}
                </Button>
              </>
            );
          })()}
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate(`/tenders/${encodeURIComponent(id)}/profit-room`)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title="Profit Room — czy się zarobi? BoQ + dostawcy + RFQ + verdict"
          >
            💰 Profit Room
          </Button>
          <Button variant="outline" size="sm" onClick={toggleWatch} disabled={actionLoading === 'watch'}>
            {actionLoading === 'watch' ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : data.is_watched ? (
              <BookmarkCheck className="w-4 h-4 mr-1 text-primary" />
            ) : (
              <BookmarkPlus className="w-4 h-4 mr-1" />
            )}
            {data.is_watched ? 'Obserwowane' : 'Obserwuj'}
          </Button>
          <FollowButton type="tender" id={id} label="Alerty" labelActive="Alerty ON" />
          {t.region && (
            <FollowButton type="region" region={t.region} label={`Follow ${t.region}`} labelActive={`${t.region} ✓`} />
          )}
          <MarkSubmittedDialog opp={{ source_id: id }} lang="pl" onDone={() => fetch()} />
          <AwardOutcomeDialog opp={{ source_id: id }} lang="pl" onDone={() => fetch()} />
          {(data.opportunity?.submitted_at) && !(data.opportunity?.award_status) && (
            <Badge className="bg-blue-500/15 text-blue-300">
              📤 Złożone {data.opportunity.submitted_at.slice(0, 10)}
              {data.opportunity.submission_reference && ` · ${data.opportunity.submission_reference}`}
            </Badge>
          )}
          {/* SPHINX Intelligence badge for contracting authority */}
          <IntelBadge
            intel={data.opportunity?.authority_intel}
            onEnrich={() => enrichTenderAuthority(id).then((r) => { fetch(); return r; })}
            lang="pl"
          />
          {(data.opportunity?.award_status || data.award_status) && (
            <Badge className={
              (data.opportunity?.award_status || data.award_status) === 'won' ? 'bg-emerald-500/15 text-emerald-300' :
              (data.opportunity?.award_status || data.award_status) === 'lost' ? 'bg-red-500/15 text-red-300' :
              'bg-amber-500/15 text-amber-300'
            }>
              {(data.opportunity?.award_status || data.award_status) === 'won' ? '🏆 Wygrana' :
               (data.opportunity?.award_status || data.award_status) === 'lost' ? '✗ Przegrana' :
               (data.opportunity?.award_status || data.award_status)}
              {(data.opportunity?.winning_price_pln || data.winning_price_pln)
                ? ` · ${Math.round(data.opportunity?.winning_price_pln || data.winning_price_pln).toLocaleString('pl-PL')} PLN`
                : ''}
            </Badge>
          )}
          {t.url && (
            <Button variant="outline" size="sm" asChild>
              <a href={t.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-1" />
                Źródło
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Euro className="w-3 h-3" /> Wartość
            </div>
            <div className="text-lg font-semibold">
              {fmtMoney(t.estimated_value, t.currency)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Calendar className="w-3 h-3" /> Termin ofert
            </div>
            <div className="text-base font-semibold">{fmtDate(t.deadline)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Tag className="w-3 h-3" /> CPV
            </div>
            <div className="text-sm font-medium line-clamp-1">
              {(t.cpv_codes || []).slice(0, 2).join(', ') || '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <FileText className="w-3 h-3" /> Załączniki
            </div>
            <div className="text-lg font-semibold">{attachments.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Action bar */}
      <Card>
        <CardContent className="pt-4 pb-4 flex items-center gap-2 flex-wrap">
          {(t.source === 'auction' || t.opportunity_type === 'auction'
            || t.source === 'bankruptcy' || t.opportunity_type === 'bankruptcy') ? (
            // Licytacje/upadłości nie mają SWZ — zamiast "Pobierz SWZ" link do obwieszczenia
            t.url ? (
              <Button asChild variant="outline" size="sm">
                <a href={t.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Zobacz obwieszczenie
                </a>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled title="Licytacje/upadłości nie mają SWZ">
                <ExternalLink className="w-4 h-4 mr-1" />
                Brak SWZ (licytacja)
              </Button>
            )
          ) : (
            <Button
              onClick={handleDownload}
              disabled={actionLoading === 'download'}
              variant={attachments.length > 0 ? 'outline' : 'default'}
              size="sm"
            >
              {actionLoading === 'download' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              {attachments.length > 0 ? 'Pobierz ponownie' : 'Pobierz SWZ'}
            </Button>
          )}
          <Button
            onClick={handleAnalyze}
            disabled={actionLoading === 'analyze' || attachments.length === 0}
            variant={analysis ? 'outline' : 'default'}
            size="sm"
            data-mission-target="btn-analyze"
            title={
              attachments.length === 0
                ? 'Brak SWZ do analizy — najpierw pobierz SWZ (Specyfikację Warunków Zamówienia)'
                : 'NLP: AI wyciągnie z SWZ wadium, terminy, kryteria, czerwone flagi'
            }
          >
            {actionLoading === 'analyze' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {analysis ? 'Re-analyze' : 'Analizuj NLP'}
          </Button>
          <Button
            onClick={handleGenerateOffer}
            disabled={actionLoading === 'offer'}
            size="sm"
            variant="default"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title="Auto-generuj draft oferty: produkty z katalogu pasujące do CPV + recommended pricing"
          >
            {actionLoading === 'offer' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Generuj ofertę
          </Button>
          <Button
            onClick={handleGenerateSwzResponse}
            disabled={actionLoading === 'swz-response' || attachments.length === 0}
            size="sm"
            variant="default"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-mission-target="btn-swz-response"
            title={
              attachments.length === 0
                ? 'Brak SWZ — najpierw pobierz załączniki'
                : 'Adam (opus-4-7) wygeneruje draft odpowiedzi oferenta: pismo przewodnie, uzasadnienie kryteriów, lista załączników, pytania Q&A'
            }
          >
            {actionLoading === 'swz-response' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Brain className="w-4 h-4 mr-1" />}
            Wygeneruj odpowiedź na SWZ
          </Button>
          {attachments.length === 0 && (
            <div className="w-full mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              <div className="font-medium text-amber-300 mb-1">Analiza wymaga najpierw SWZ (dokumentacji przetargu)</div>
              <div className="text-muted-foreground leading-snug">
                Kliknij <b>„Pobierz SWZ"</b> powyżej. Jeśli pobranie z BZP nie zadziała
                (eZamowienia czasem zmienia API), pobierz dokumenty <b>ręcznie ze strony ogłoszenia</b>
                {t.url ? <> → <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">otwórz ogłoszenie</a></> : null}.
                Dopóki nie ma SWZ, przycisk „Analizuj" jest nieaktywny — to nie błąd, tylko brak danych do analizy.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============================================================
            5 grup zamiast 12 tabów (2026-05-14 reorg Etap 1)
            Deep-link backward compat — handler poniżej mapuje stare
            wartości (?tab=consilium) na nowe (?group=analysis&sub=consilium)
         ============================================================ */}
      {(() => {
        // Mapa starych wartości → (group, sub)
        const legacyMap = {
          overview:    ['overview',  'about'],
          connections: ['overview',  'connections'],
          chain:       ['overview',  'chain'],
          attachments: ['documents', 'attachments'],
          changes:     ['documents', 'changes'],
          qa:          ['documents', 'qa'],
          analysis:    ['analysis',  'nlp'],
          prediction:  ['analysis',  'prediction'],
          consilium:   ['analysis',  'consilium'],
          pricing:     ['pricing',   'profit'],
          actions:     ['pricing',   'actions'],
          history:     ['history',   'timeline'],
        };
        // Read ?tab=... from URL (legacy deep link)
        const urlTab = new URLSearchParams(window.location.search).get('tab');
        const [defaultGroup, defaultSub] = (legacyMap[urlTab] || ['overview', 'about']);
        return (
          <Tabs defaultValue={defaultGroup} className="space-y-3">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="overview" data-testid="tab-group-overview">
                📋 Przegląd
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-group-documents">
                <span className="flex items-center gap-1">
                  📄 Dokumenty
                  {attachments.length > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      {attachments.length}
                    </Badge>
                  )}
                  {unackChanges > 0 && (
                    <Badge className="bg-amber-500 text-amber-950 text-[10px] h-4 px-1 ml-0.5">
                      {unackChanges}
                    </Badge>
                  )}
                </span>
              </TabsTrigger>
              <TabsTrigger value="analysis" data-testid="tab-group-analysis">
                🧠 Analiza
              </TabsTrigger>
              <TabsTrigger value="pricing" data-testid="tab-group-pricing">
                💰 Wycena i akcje
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-group-history">
                📜 Historia
              </TabsTrigger>
            </TabsList>

            {/* ════════════════════════════════════════════════════════
                 GRUPA 1: PRZEGLĄD (Opis + Powiązania + Łańcuch)
                ════════════════════════════════════════════════════════ */}
            <TabsContent value="overview" className="space-y-2">
              <Tabs defaultValue={defaultSub === 'about' || defaultSub === 'connections' || defaultSub === 'chain' ? defaultSub : 'about'}>
                <TabsList className="grid grid-cols-3 w-full md:w-auto md:inline-flex">
                  <TabsTrigger value="about">Opis</TabsTrigger>
                  <TabsTrigger value="connections">Powiązania</TabsTrigger>
                  <TabsTrigger value="chain">
                    <Users className="w-3 h-3 mr-1" />
                    Łańcuch decyzyjny
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="about" className="mt-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Opis zamówienia</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                        {t.description || '—'}
                      </p>
                      {t.cpv_codes?.length > 0 && (
                        <div className="mt-4 pt-3 border-t">
                          <div className="text-xs text-muted-foreground mb-1.5">Wszystkie kody CPV:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {t.cpv_codes.map((c, i) => (
                              <Badge key={i} variant="outline">{c}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {t.keywords_matched?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="text-xs text-muted-foreground mb-1.5">Dopasowane słowa kluczowe:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {t.keywords_matched.map((k, i) => (
                              <Badge key={i} variant="outline" className="bg-primary/10 text-primary border-primary/30">{k}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="connections" className="mt-3">
                  <ConnectionsPanel oppId={id} lang="pl" />
                </TabsContent>

                <TabsContent value="chain" className="mt-3">
                  <DecisionChainPanel tenderId={id} />
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ════════════════════════════════════════════════════════
                 GRUPA 2: DOKUMENTY (Załączniki + Zmiany SWZ + Q&A)
                ════════════════════════════════════════════════════════ */}
            <TabsContent value="documents" className="space-y-2">
              <Tabs defaultValue={defaultSub === 'changes' || defaultSub === 'qa' ? defaultSub : 'attachments'}>
                <TabsList className="grid grid-cols-3 w-full md:w-auto md:inline-flex">
                  <TabsTrigger value="attachments">
                    Załączniki {attachments.length > 0 && `(${attachments.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="changes">
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Zmiany SWZ
                      {unackChanges > 0 && (
                        <Badge className="bg-amber-500 text-amber-950 text-[10px] h-4 px-1">
                          {unackChanges}
                        </Badge>
                      )}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger value="qa">Q&A z zamawiającym</TabsTrigger>
                </TabsList>

                <TabsContent value="attachments" className="mt-3">
                  {attachments.length === 0 ? (
                    <Card>
                      <CardContent className="pt-10 pb-10 text-center">
                        <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">Brak pobranych załączników</p>
                        <Button variant="outline" size="sm" onClick={handleDownload} disabled={actionLoading === 'download'} className="mt-3">
                          {actionLoading === 'download' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                          Pobierz SWZ teraz
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map(att => (
                        <Card key={att.id}>
                          <CardContent className="pt-4 pb-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <div className="font-medium text-sm">{att.filename}</div>
                                <div className="text-xs text-muted-foreground">
                                  {(att.size_bytes / 1024).toFixed(1)} KB ·
                                  {att.parse_status === 'parsed' && ` ${att.page_count} stron, ${(att.text_length / 1000).toFixed(0)}k znaków`}
                                  {att.parse_status === 'pending' && ' czeka na analizę'}
                                  {att.indexed_at && ` · indexed: ${att.index_chunk_count} chunków`}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPdfOpen(pdfOpen === att.id ? null : att.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {pdfOpen === att.id ? 'Zamknij' : 'Podgląd'}
                            </Button>
                          </CardContent>
                          {pdfOpen === att.id && (
                            <CardContent className="pt-0 pb-0">
                              <iframe
                                src={getTenderAttachmentPdfUrl(id, att.id) + `?token=${localStorage.getItem('token') || ''}`}
                                title={att.filename}
                                className="w-full h-[700px] border rounded"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Jeśli PDF się nie ładuje (blokada CORS na iframe),
                                <a
                                  href={getTenderAttachmentPdfUrl(id, att.id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline ml-1"
                                >
                                  otwórz w nowej karcie
                                </a>.
                              </p>
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="changes" className="mt-3">
                  <PlanLock feature="tenders_swz_changes" requiredPlan="pro" lang={lang}>
                    <ChangesPanel
                      tenderId={id}
                      lang={lang || 'pl'}
                      onUnacknowledgedCountChange={setUnackChanges}
                    />
                  </PlanLock>
                </TabsContent>

                <TabsContent value="qa" className="mt-3">
                  <QAPanel tenderId={id} />
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ════════════════════════════════════════════════════════
                 GRUPA 3: ANALIZA (NLP + Predykcja + Consilium AI)
                ════════════════════════════════════════════════════════ */}
            <TabsContent value="analysis" className="space-y-2">
              <Tabs defaultValue={defaultSub === 'prediction' || defaultSub === 'consilium' ? defaultSub : 'nlp'}>
                <TabsList className="grid grid-cols-3 w-full md:w-auto md:inline-flex">
                  <TabsTrigger value="nlp">Analiza NLP</TabsTrigger>
                  <TabsTrigger value="prediction">Predykcja win-rate</TabsTrigger>
                  <TabsTrigger value="consilium">
                    <Brain className="w-3 h-3 mr-1" />
                    Consilium AI
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="nlp" className="mt-3">
                  <AnalysisPanel analysis={analysis} />
                </TabsContent>

                <TabsContent value="prediction" className="mt-3">
                  <PredictionPanel tenderId={id} />
                </TabsContent>

                <TabsContent value="consilium" className="mt-3">
                  <PlanLock feature="tenders_consilium" requiredPlan="pro" lang={lang}>
                    {attachments.length === 0 ? (
                      <Card>
                        <CardContent className="pt-10 pb-10 text-center">
                          <Brain className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Najpierw pobierz SWZ</p>
                          <Button variant="outline" size="sm" onClick={handleDownload} disabled={actionLoading === 'download'} className="mt-3">
                            <Download className="w-4 h-4 mr-1" />
                            Pobierz SWZ teraz
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <ConsiliumPanel tenderId={id} tenderTitle={t.title} />
                    )}
                  </PlanLock>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ════════════════════════════════════════════════════════
                 GRUPA 4: WYCENA I AKCJE (Pricing + Outreach/JEDZ)
                ════════════════════════════════════════════════════════ */}
            <TabsContent value="pricing" className="space-y-2">
              <Tabs defaultValue={defaultSub === 'actions' ? 'actions' : 'profit'}>
                <TabsList className="grid grid-cols-2 w-full md:w-auto md:inline-flex">
                  <TabsTrigger value="profit">💰 Pricing & Intratność</TabsTrigger>
                  <TabsTrigger value="actions">📨 Outreach + JEDZ</TabsTrigger>
                </TabsList>

                <TabsContent value="profit" className="mt-3">
                  <PricingPanel tenderId={id} />
                </TabsContent>

                <TabsContent value="actions" className="mt-3">
                  <ActionsPanel tenderId={id} />
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ════════════════════════════════════════════════════════
                 GRUPA 5: HISTORIA (Timeline)
                ════════════════════════════════════════════════════════ */}
            <TabsContent value="history">
              <TimelinePanel tenderId={id} />
            </TabsContent>
          </Tabs>
        );
      })()}

      {/* MASKIL Agent — SWZ response dialog (D-track 2026-05-26) */}
      <Dialog open={swzResponseDialog} onOpenChange={setSwzResponseDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-500" />
              Adam — Draft odpowiedzi na SWZ
            </DialogTitle>
          </DialogHeader>
          {actionLoading === 'swz-response' && !swzResponseText ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              <p className="text-sm text-muted-foreground">
                Adam analizuje SWZ i komponuje odpowiedź… (opus-4-7, ~20-40 sekund)
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-3 text-xs">
                <div>
                  <label className="font-medium block mb-1">Nazwa firmy oferenta (opcjonalnie)</label>
                  <input
                    type="text"
                    className="w-full px-2 py-1 rounded border border-border bg-background text-sm"
                    placeholder="np. AcmeLED Sp. z o.o."
                    value={userCompany}
                    onChange={e => setUserCompany(e.target.value)}
                  />
                </div>
                <div>
                  <label className="font-medium block mb-1">Dodatkowe wytyczne dla Adama (opcjonalnie)</label>
                  <Textarea
                    placeholder="np. 'Podkreśl 5-letnią gwarancję' albo 'Sugeruj cenę 12% niżej od średniej'"
                    value={extraNotes}
                    onChange={e => setExtraNotes(e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerateSwzResponse}
                  disabled={actionLoading === 'swz-response'}
                >
                  Wygeneruj ponownie z tymi wytycznymi
                </Button>
              </div>

              <Textarea
                value={swzResponseText}
                onChange={e => setSwzResponseText(e.target.value)}
                rows={18}
                className="font-mono text-xs"
                placeholder="Generowanie…"
              />

              {swzResponseMeta && (
                <div className="text-[11px] text-muted-foreground space-y-0.5 pt-2 border-t border-border">
                  <div>Model: <span className="font-mono">{swzResponseMeta.model}</span> · rola: <Badge variant="outline" className="text-[10px]">{swzResponseMeta.role}</Badge></div>
                  <div>Tokens: in={swzResponseMeta.input_tokens} / out={swzResponseMeta.output_tokens}{swzResponseMeta.cache_hit && ' · cache hit'}</div>
                  {swzResponseMeta.ctx && (
                    <div>Kontekst: analiza={swzResponseMeta.ctx.analysis_present ? '✓' : '–'} · załączniki={swzResponseMeta.ctx.attachments_count} · {swzResponseMeta.ctx.external_context_chars} chars</div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={handleCopySwz}>
                  Kopiuj do schowka
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSwzResponseDialog(false)}>
                  Zamknij
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
